/**
 * A thin hook that reads the `confluence_cloud_site_has_over_10_enabled_users` TAP site trait
 * and returns a boolean indicating whether the site has more than 10 enabled users.
 *
 * The trait value is a boolean string:
 *   "true"  → site has >10 enabled users
 *   "false" → site has ≤10 enabled users
 *
 * This is SSR-compatible: `useSiteTraits()` reads from `window.__SSR_TAP_CONTEXT__` when available,
 * so there is no client-side flash on initial load.
 */
import { useSiteTraits } from './useSiteTraits.ts';

export const OVER_TEN_ENABLED_USERS_TRAIT = 'confluence_cloud_site_has_over_10_enabled_users';

export type UseIsOverTenEnabledUsersResult = {
	/** true = >10 EU, false = ≤10 EU, null = trait not yet available or missing */
	isOverTenEU: boolean | null;
	loading: boolean;
	error: Error | null;
};

export function useIsOverTenEnabledUsers({
	skipBootstrap,
}: { skipBootstrap?: boolean } = {}): UseIsOverTenEnabledUsersResult {
	const { data, loading, error } = useSiteTraits({ skipBootstrap });

	const traitValue = data?.attributes?.find(
		(attr) => attr.name === OVER_TEN_ENABLED_USERS_TRAIT,
	)?.value;

	const isOverTenEU = traitValue !== undefined ? traitValue === 'true' : null;

	return { isOverTenEU, loading, error };
}
